import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarCheck2, Ticket } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvciUyMGRlbnRpc3QlMjBwYXRpZW50fGVufDB8fHx8MTc5MTM4OTE0NHww&ixlib=rb-4.1.0&q=85";

const lineReveal = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.12 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.5 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const AppointmentCard = () => (
  <div className="w-[280px] rounded-2xl border border-line bg-white/95 shadow-lift backdrop-blur-xl sm:w-[300px]">
    <div className="flex items-center gap-1.5 border-b border-line/70 px-4 py-3">
      <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
      <span className="ml-2 font-mono text-[10px] font-medium text-ink/45">
        AssisDent — Book an appointment
      </span>
    </div>
    <div className="space-y-3 p-4">
      <div className="flex items-center gap-2.5">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-mint font-display text-sm font-semibold text-brand-deep">
          SM
        </span>
        <div>
          <p className="text-sm font-semibold text-ink">Dental check-up</p>
          <p className="text-xs text-ink/45">General dentistry</p>
        </div>
      </div>
      <div className="flex gap-1.5">
        {["Mon 12", "Tue 13", "Wed 14"].map((d, i) => (
          <span
            key={d}
            className={`flex-1 rounded-lg border px-2 py-1.5 text-center text-[11px] font-medium transition-colors ${
              i === 1
                ? "border-brand bg-brand/10 text-brand-deep"
                : "border-line bg-paper text-ink/50"
            }`}
          >
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {["9:00", "9:40", "10:20", "11:00", "13:20", "14:00"].map((t, i) => (
          <span
            key={t}
            className={`rounded-lg border px-2 py-1.5 text-center text-[11px] font-medium ${
              i === 4
                ? "border-brand bg-brand text-white"
                : "border-line bg-paper text-ink/50"
            }`}
          >
            {t}
          </span>
        ))}
      </div>
      <div className="rounded-xl bg-brand py-2.5 text-center text-xs font-semibold text-white">
        Confirm booking
      </div>
    </div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, -36]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const yChip = useTransform(scrollYProgress, [0, 1], [0, -55]);

  const onBook = () => {
    scrollToSection("register");
  };

  return (
    <section ref={ref} className="relative overflow-hidden pb-20 pt-[130px] sm:pt-[150px] lg:pb-28">
      <div
        className="pointer-events-none absolute -top-40 right-[-10%] h-[640px] w-[640px] rounded-full opacity-60"
        style={{ background: "radial-gradient(circle, rgba(204,251,241,0.75) 0%, rgba(250,253,251,0) 65%)" }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(11,42,38,0.10) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
        }}
      />
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-6">
          <motion.div variants={fadeUp} custom={0} initial="hidden" animate="show" className="flex items-center gap-2.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-bright opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brand" />
            </span>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-deep">
              5,000+ clinics · across the USA
            </p>
          </motion.div>

          <h1 className="mt-6 font-display text-[clamp(2.9rem,7vw,5.4rem)] font-medium leading-[1.02] tracking-tight">
            {["Your dentist,", "one tap away."].map((l, i) => (
              <span key={l} className="block overflow-hidden pb-1">
                <motion.span
                  variants={lineReveal}
                  custom={i}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 1 ? "italic text-brand" : ""}`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="show"
            className="mt-6 max-w-md text-lg leading-relaxed text-ink/60"
          >
            AssisDent connects you with thousands of dental clinics across the USA — book an
            appointment in seconds and get a discount on your first booking.
          </motion.p>

          <motion.div variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-9 flex flex-wrap items-center gap-4">
            <button
              onClick={onBook}
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-2.5 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-paper shadow-lift transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-brand-deep"
            >
              <CalendarCheck2 className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
              Book now
            </button>
            <button
              onClick={() => scrollToSection("about")}
              data-testid="hero-about-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-4 text-sm font-semibold text-ink transition-[border-color,background-color] duration-200 hover:border-brand hover:bg-mint-soft"
            >
              About us
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-6 lg:h-[620px]">
          <motion.div style={{ y: yPhoto }} className="relative mx-auto w-[86%] max-w-[420px] lg:absolute lg:right-0 lg:top-2 lg:w-[74%]">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-b-[36px] rounded-t-[200px] border border-line shadow-lift"
            >
              <img
                src={HERO_IMAGE}
                alt="Modern dental clinic interior"
                className="h-[420px] w-full object-cover sm:h-[520px]"
              />
              <div
                className="pointer-events-none absolute inset-0 rounded-b-[36px] rounded-t-[200px]"
                style={{ background: "linear-gradient(160deg, rgba(11,42,38,0) 55%, rgba(11,42,38,0.35) 100%)" }}
              />
            </motion.div>
          </motion.div>

          <motion.div
            style={{ y: yCard }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 mx-auto mt-8 w-fit lg:absolute lg:-left-2 lg:top-24 lg:mt-0"
          >
            <div className="animate-floaty">
              <AppointmentCard />
            </div>
          </motion.div>

          <motion.div
            style={{ y: yChip }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 mx-auto mt-6 w-fit lg:absolute lg:-left-6 lg:bottom-16 lg:mt-0"
          >
            <div
              className="animate-floaty flex items-center gap-2 rounded-full border border-gold-border bg-gold-bg px-4 py-2.5 text-sm font-semibold text-[#92400E] shadow-soft"
              style={{ animationDelay: "1.2s" }}
              data-testid="hero-discount-chip"
            >
              <Ticket className="h-4 w-4" />
              First-visit discount
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
